import React, { useEffect, useState, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { setLoading, storeHeaderActions, storeSearchInvoice } from 'store/reducers/mainSlice';
import { FaSearch } from 'react-icons/fa';
import { Skeleton } from '@mui/material';

import { CommonDateOnlyFormat } from 'helpers/common.helper';
import { TbDatabaseExclamation } from 'react-icons/tb';
import usePageTitle from 'hooks/usePageTitle';
import SortableHeader from 'ui-component/SortableHeader';
import moment from 'moment';

import NoRecordFoundIcon from 'assets/images/icons/no-records-found.svg';
import StatusBadge from 'ui-component/StatusBadge';
import useIsMobile from 'utils/isMobileUtils';


const throttle = (func, delay) => {
	let lastCall = 0;
	return (...args) => {
		const now = new Date().getTime();
		if (now - lastCall < delay) {
			return;
		}
		lastCall = now;
		return func(...args);
	};
};

const index = () => {
	const [setPageTitle] = usePageTitle('Shipments');
	useEffect(() => {
		setPageTitle('Shipments');
		return () => {
			setPageTitle(null);
		};
	}, []);
	const dispatch = useDispatch();
	const isMobile = useIsMobile();

	const [searchField, setSearchField] = useState();
	const [searchValue, setSearchValue] = useState();
	const [searchTimeout, setSearchTimeout] = useState(null);
	const headerActions = useSelector((state) => state.main.headerActions);
	const storedSearch = useSelector((state) => state.main.searchInvoice);
	const storedFilter = useSelector((state) => state.main.filterInvoice);
	const [sortByFilter, setSortByFilter] = useState({ field: 'created_at', direction: 'desc' });

	const [dataList, setDataList] = useState([]);
	const [page, setPage] = useState(1);
	const [hasMore, setHasMore] = useState(true);
	const tableRef = useRef();
	const [showPreloadingRows, setShowPreloadingRows] = useState(true);
	const [totalRecords, setTotalRecords] = useState(0);
	const [isLoading, setIsLoading] = useState(true);


	const loadMoreData = useCallback(() => {
		if (showPreloadingRows || !hasMore) return;
		setShowPreloadingRows(true);
		let request = {};
		request.page = page + 1;
		request.search = searchField?.length > 2 ? searchField : null;

		api.getClientShipments(request, searchField, sortByFilter).then((res) => {
				const newRecords = res?.data?.data?.data || [];
				const meta = res?.data?.data?.meta;
				const nextPage = meta?.current_page || request.page;
				const lastPage = meta?.last_page || nextPage;
				setDataList((prev) => [...prev, ...newRecords]);
				if (nextPage >= lastPage || newRecords.length === 0) {
					setHasMore(false);
				} else {
					setPage(nextPage);
				}
			})
			.catch((err) => {
				console.error('Error fetching more shipments:', err);
				setHasMore(false);
			})
			.finally(() => {
				setShowPreloadingRows(false);
			});
	}, [page, hasMore, searchField, sortByFilter, showPreloadingRows]);

	const initializeData = useCallback(() => {
		setIsLoading(true);
		setShowPreloadingRows(true);

		let request = {};
		request.page = 1;
		request.search = searchField?.length > 2 ? searchField : null;

		api
			.getClientShipments(request, searchField, sortByFilter)
			.then((res) => {
				const meta = res?.data;
				const newRecords = meta?.data || [];
				setDataList(newRecords);
				setTotalRecords(meta?.total || 0);
				const nextPage = meta?.current_page || 1;
				const lastPage = meta?.last_page || nextPage;
				setPage(nextPage);
				setHasMore(nextPage < lastPage);
			})
			.catch((err) => {
				console.error('Error fetching shipments:', err);
				setDataList([]);
				setTotalRecords(0);
				setHasMore(false);
			})
			.finally(() => {
				dispatch(setLoading(false));
				setIsLoading(false);
				setShowPreloadingRows(false);
			});
	}, [dispatch, searchField, sortByFilter]);

	const handleSearch = (e) => {
		let text = e.target.value;

		setIsLoading(true);
		setSearchValue(text);
		if (searchTimeout) {
			clearTimeout(searchTimeout);
		}

		let timeout = setTimeout(() => {
			dispatch(storeSearchInvoice(text));
			setSearchField(text);
		}, 500);

		setSearchTimeout(timeout);
	};

	const updateSortByStatus = (name) => {
		if (sortByFilter?.field === name) {
			setSortByFilter((e) => ({
				...e,
				direction: e?.direction === 'asc' ? 'desc' : 'asc'
			}));
		} else {
			setSortByFilter((e) => ({
				field: name,
				direction: 'asc'
			}));
		}
	};

	useEffect(() => {
		setSearchField(storedSearch);
		initializeData();
		return () => {
			if (searchTimeout) clearTimeout(searchTimeout);
		};
	}, [storedSearch, storedFilter, sortByFilter, initializeData]);

	useEffect(() => {
		const handleScroll = () => {
			if (!tableRef.current || showPreloadingRows || !hasMore) return;
			const { scrollTop, scrollHeight, clientHeight } = tableRef.current;
			if (scrollTop + clientHeight >= scrollHeight - 100) {
				loadMoreData();
			}
		};
		const throttledHandleScroll = throttle(handleScroll, 200);
		const currentRef = tableRef.current;
		if (currentRef) {
			currentRef.addEventListener('scroll', throttledHandleScroll);
		}
		return () => {
			if (currentRef) {
				currentRef.removeEventListener('scroll', throttledHandleScroll);
			}
		};
	}, [showPreloadingRows, hasMore, loadMoreData]);

	useEffect(() => {
		dispatch(
			storeHeaderActions({
				showQuickStats: false,
				showExport: headerActions?.showExport
			})
		);
	}, [searchField, sortByFilter]);

	return (
		<div className="row">
			<div className="col-xl-12 col-lg-12">
				<div className="show-on-mobile new-page-header" style={{ position: 'sticky' }}>
					<div className="client-header-bubble" />
					<div className="d-flex flex-column gap-2 " style={{ position: 'sticky', zIndex: 1, paddingTop: 10, width: '100%' }}>
						<h5 style={{ textAlign: 'center' }}>Shipments</h5>
						<div
							style={{
								paddingLeft: 20,
								paddingRight: 20,
								display: 'flex',
								justifyContent: 'center',
								alignItems: 'center',
								gap: 10,
								paddingBottom: 4
							}}
						/>
					</div>
				</div>

				<div
					style={{
						display: 'flex',
						justifyContent: 'space-between',
						alignItems: 'center',
						gap: 10,
						paddingTop: 0,
						paddingBottom: 5,
						paddingLeft: 20,
						paddingRight: 20
					}}
					className="show-on-mobile"
				>
					<form className="form-inline navbar-search" style={{ flex: 1 }}>
						<div style={{ position: 'relative', width: '100%' }}>
							<input
								type="text"
								className="form-control bg-white border"
								placeholder="Search"
								aria-label="Search"
								aria-describedby="basic-addon2"
								value={searchValue || ''}
								onChange={handleSearch}
								style={{ width: '100%', paddingRight: 36 }}
							/>
							<span
								className="searchIcon"
								style={{
									position: 'absolute',
									right: 10,
									top: '50%',
									transform: 'translateY(-50%)',
									color: '#888',
									pointerEvents: 'none'
								}}
							>
								<FaSearch />
							</span>
						</div>
					</form>
					<div style={{ flexShrink: 0 }}> </div>
				</div>
				<div className="flex justify-between" style={{ marginTop: '10px' }}>
					<h5
						className="hide-on-mobile"
						style={{
							color: '#17181A',
							fontWeight: 600,
							marginTop: '10px',
							marginLeft: '5px'
						}}
					>
						Shipments
					</h5>
				</div>

				<div className={`card shadow-sm  bg-white table-card mt-3 ${isMobile ? 'shorter-table' : ''}`}>
					<div className=" align-items-center justify-content-end TableSerachBar">
						<form className="form-inline navbar-search" style={{ display: 'flex', gap: '5px' }} onSubmit={(e) => e.preventDefault()}>
							<div className="input-group">
								<input
									type="text"
									className="form-control bg-white border"
									placeholder="Search"
									aria-label="Search"
									aria-describedby="basic-addon2"
									value={searchValue || ''}
									onChange={handleSearch}
									style={{ width: '100%' }}
								/>
								<span className="searchIcon">
									<FaSearch />
								</span>
							</div>
						</form>
					</div>

					<div className="estimation-table-area">
						<div className="table-side">
							{' '}
							{!isMobile ? (
								<div
									className="table-responsive-xl tableData estimationList mt-3"
									ref={tableRef}
									style={{
										height: 'calc(100vh - 312px)',
										overflowY: 'auto',
										scrollbarWidth: 'thin',
										scrollbarColor: '#0966ff transparent'
									}}
								>
									<table className="table  min-width-unset tbl-space pm-table d-none d-md-table">
										<thead>
											<tr style={{ height: '35px' }} className="sticky-header">
												<th scope="col" width="">
													<SortableHeader name={`provider`} currentSortBy={sortByFilter} onClick={() => updateSortByStatus('provider')}>
														Provider
													</SortableHeader>
												</th>
												<th scope="col" width="">
													<SortableHeader
														name="tracking_number"
														currentSortBy={sortByFilter}
														onClick={() => updateSortByStatus('tracking_number')}
													>
														Tracking Number
													</SortableHeader>
												</th>
												<th scope="col">
													<SortableHeader name="reference" currentSortBy={sortByFilter} onClick={() => updateSortByStatus('reference')}>
														Reference
													</SortableHeader>
												</th>
												<th scope="col">
													<SortableHeader name="weight" currentSortBy={sortByFilter} onClick={() => updateSortByStatus('weight')}>
														Weight
													</SortableHeader>
												</th>
												<th scope="col">
													<SortableHeader name="created_at" currentSortBy={sortByFilter} onClick={() => updateSortByStatus('created_at')}>
														Shipped Date
													</SortableHeader>
												</th>
											</tr>
										</thead>
										<tbody>
											{isLoading &&
												[...Array(15)].map(
													(
														e,
														i
													) => (
														<tr style={{ height: '35px' }} key={`skel-${i}`}>
															{[...Array(5)].map((e, i) => (
																<td key={`skel-td-${i}`}>
																	<Skeleton variant="rounded" width={'100%'} height={15} style={{ borderRadius: '32px' }} />
																</td>
															))}
														</tr>
													)
												)}

											{!isLoading &&
												dataList?.length > 0 &&
												dataList?.map((item, index) => {
													const shipmentRef = item?.invoice_number;
													const trackingNumber = item?.tracking_no || 'N/A';
													const trackingLink = item?.tracking_link;

													const handleRowClick = (e, trackingLink) => {
														// Prevent clicks on inner links from triggering the row click
														if (e.target.tagName === 'A') return;

														if (trackingLink) {
															window.open(trackingLink, '_blank', 'noopener,noreferrer');
														}
													};

													return (
														<tr
															style={{ height: '35px' }}
															key={index}
															className="cursor-pointer"
															onClick={(e) => handleRowClick(e, trackingLink)}
														>
															<td className={`align-middle font14`}>{item?.reference_shipping_company?.name || '-'}</td>

															<td className={`align-middle font14`}>
																{trackingLink ? (
																			<a
																				href={trackingLink}
																				target="_blank"
																				rel="noopener noreferrer"
																				onClick={(e) => e.stopPropagation()}
																			>
																		{trackingNumber}
																	</a>
																) : (
																	<>{trackingNumber}</>
																)}
															</td>

															<td className={`align-middle font14`}>
																<Link
																	to={`/client/invoices/${shipmentRef}`}
																	className="text-primary"
																	onClick={(e) => e.stopPropagation()}
																>
																	{shipmentRef || '-'}
																</Link>
															</td>

															<td className={`align-middle font14`}>{item.weight ? `${item.weight} ${item.weight_unit}` : 'N/A'}</td>
															<td className="align-middle font14">
																{item?.created_at ? moment(item.created_at).format(CommonDateOnlyFormat) : '-'}
															</td>
														</tr>
													);
												})}

											{showPreloadingRows &&
												!isLoading &&
												hasMore &&
												[...Array(50)].map((e, i) => (
													<tr style={{ height: '35px' }} key={`pre-${i}`}>
														{[...Array(5)].map((e, i) => (
															<td key={`pre-td-${i}`}>
																<Skeleton variant="rounded" width={'100%'} height={15} style={{ borderRadius: '32px' }} />
															</td>
														))}
													</tr>
												))}

											{dataList?.length < 1 && !isLoading && (
												<tr className="no-hover">
													<td colSpan={5}>
														<div className="not-found-container">
															<div className="not-found-icon">
																<img src={NoRecordFoundIcon} style={{ height: '200px', width: '200px' }} alt="No Records Found" />
															</div>
															<h6 className="not-found-text">No Data Available</h6>
														</div>
													</td>
												</tr>
											)}
										</tbody>
									</table>
								</div>
							) : (
								<div
									className="table-responsive-xl tableData estimationList"
									ref={tableRef}
									style={{
										height: `calc(100dvh - 281px)`,
										overflowY: 'auto',
										display: 'flex',
										flexDirection: 'column'
									}}
								>
									{isLoading ? (
										<div className="inv-cards-list d-md-none">
											{[...Array(15)].map((_, idx) => (
												<div
													key={idx}
													className="order-list-card"
													style={{
														background: idx % 2 === 0 ? '#f9f9f9' : '#fff',
														borderRadius: 12,
														padding: 16,
														display: 'flex',
														flexDirection: 'column',
														gap: 10
													}}
												>
													{[...Array(2)].map((__, i) => (
														<div key={i} style={{ width: i === 0 ? '60%' : '40%' }}>
															<Skeleton variant="rounded" width={'100%'} height={18} style={{ borderRadius: '8px' }} />
														</div>
													))}
												</div>
											))}
										</div>
									) : (
										<div className="inv-cards-list d-md-none">
											{dataList?.length > 0 &&
												dataList?.map((item, index) => {
													const trackingNumber = item?.tracking_no || '-';
													const trackingLink = item?.tracking_link;
													const shipmentRef = item?.invoice_number;

													const handleCardClick = (e) => {
														if (e.target.tagName === 'A') return;
														if (trackingLink) window.open(trackingLink, '_blank');
													};

													return (
														<div
															className="order-list-card active-action"
															key={index}
															onClick={handleCardClick}
															style={{
																background: index % 2 === 0 ? '#f9f9f9' : '#fff'
															}}
														>
															<div
																className="order-list-card-body"
																style={{
																	display: 'flex',
																	justifyContent: 'space-between',
																	width: '100%'
																}}
															>
																<div
																	style={{
																		display: 'grid',
																		gridTemplateColumns: '75px auto'
																		// rowGap: '4px'
																	}}
																>
																	<span className="order-customer" style={{ fontWeight: '130px' }}>
																		Tracking :
																	</span>
																	<span className="order-customer">
																		{trackingLink && trackingNumber !== '-' ? (
																			<a
																				href={trackingLink}
																				target="_blank"
																				rel="noopener noreferrer"
																				className="text-primary"
																				onClick={(e) => e.stopPropagation()}
																			>
																				{trackingNumber}
																			</a>
																		) : (
																			trackingNumber
																		)}
																	</span>

																	<span className="order-customer">Reference :</span>
																	<span className="order-customer">
																		<Link
																			to={`/client/invoices/${shipmentRef}`}
																			className="text-primary"
																			onClick={(e) => e.stopPropagation()}
																		>
																			{shipmentRef || '-'}
																		</Link>
																	</span>

																	<span className="order-customer">Provider :</span>
																	<span className="order-customer">{item?.reference_shipping_company?.name || '-'}</span>
																</div>
																{/* RIGHT SIDE — Date, Weight, Status */}
																<div
																	style={{
																		textAlign: 'right',
																		display: 'flex',
																		flexDirection: 'column',
																		gap: 2,
																		alignItems: 'flex-end',
																		marginTop: 'auto'
																	}}
																>
																	<div className="order-date" style={{ fontSize: '10px', fontWeight: '300' }}>
																		{item?.created_at ? moment(item.created_at).format(CommonDateOnlyFormat) : '-'}
																	</div>

																	<div className="order-customer" style={{ color: '#8f8b8b' }}>
																		{item?.weight ? `${item.weight} Kg` : '-'}
																	</div>

																	<div className="total-val">
																		<StatusBadge itemStatus={item?.master_status?.name} />
																	</div>
																</div>
															</div>
														</div>
													);
												})}

											{showPreloadingRows &&
												!isLoading &&
												hasMore &&
												[...Array(5)].map((_, idx) => (
													<div
														key={`pre-mob-${idx}`}
														className="order-list-card"
														style={{
															background: idx % 2 === 0 ? '#f9f9f9' : '#fff',
															borderRadius: 12,
															padding: 16,
															display: 'flex',
															flexDirection: 'column',
															gap: 10
														}}
													>
														{[...Array(2)].map((__, i) => (
															<div key={`pre-mob-skel-${i}`} style={{ width: i === 0 ? '60%' : '40%' }}>
																<Skeleton variant="rounded" width={'100%'} height={18} style={{ borderRadius: '8px' }} />
															</div>
														))}
													</div>
												))}

											{dataList?.length < 1 && !isLoading && (
												<div className="w-100">
													<div className="not-found-container">
														<div className="not-found-icon">
															<TbDatabaseExclamation />
														</div>
														<h6 className="not-found-text">No Data Available</h6>
													</div>
												</div>
											)}
										</div>
									)}
								</div>
							)}
							<div
								className="hide-on-mobile"
								style={{
									padding: '10px 0 0 20px',
									borderTop: '1px solid #e0e0e0e0',
									display: 'flex',
									gap: '5px',
									justifyContent: 'flex-end',
									height: '40px'
								}}
							>
								<div className="flex align-items-center ml-auto" style={{ gap: '5px', paddingRight: '25px' }}>
									{isLoading ? (
										<div style={{ width: '100px' }}>
											<Skeleton variant="rounded" width={'100%'} height={20}></Skeleton>
										</div>
									) : (
										<>
											<div className="text-gray-800">Total Record:</div>
											<div className="fw-500">{totalRecords.toLocaleString('en-US')}</div>
										</>
									)}
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>
	);
};

export default index;
